import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";
import { AIR, Readout, AirFontImport, apiErrorMessage } from "./airTheme";
import FlightHistory from "./FlightHistory";

// Global app navbar is `fixed top-0 h-16` (see ResuableCompoment/Navbar.js) — sticky
// elements must offset by this or they stick underneath/behind it.
const NAVBAR_HEIGHT = 64;

// ─── Icon system ────────────────────────────────────────────────────
const Icon = ({ d, size = 16, strokeWidth = 1.8, style, ...rest }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, ...style }} {...rest}>
    {d}
  </svg>
);
const IcArrowLeft = (p) => <Icon {...p} d={<><path d="M19 12H5" /><polyline points="12 19 5 12 12 5" /></>} />;
const IcSliders = (p) => <Icon {...p} d={<>
  <line x1="4" y1="6" x2="20" y2="6" /><circle cx="9" cy="6" r="2" fill="currentColor" stroke="none" />
  <line x1="4" y1="12" x2="20" y2="12" /><circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" />
  <line x1="4" y1="18" x2="20" y2="18" /><circle cx="7" cy="18" r="2" fill="currentColor" stroke="none" />
</>} />;
const IcMapPin = (p) => <Icon {...p} d={<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>} />;
const IcLayers = (p) => <Icon {...p} d={<><polygon points="12 2 2 7 12 12 22 7 12 2" /><polyline points="2 17 12 22 22 17" /><polyline points="2 12 12 17 22 12" /></>} />;
const IcMoon = (p) => <Icon {...p} d={<path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />} />;
const IcSunrise = (p) => <Icon {...p} d={<><circle cx="12" cy="14" r="4" /><path d="M12 4v4M5 14h.01M19 14h.01M6.3 9.3l1.4 1.4M17.7 9.3l-1.4 1.4" /><line x1="2" y1="20" x2="22" y2="20" /><polyline points="9 8 12 5 15 8" /></>} />;
const IcSun = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>} />;
const IcSunset = (p) => <Icon {...p} d={<><circle cx="12" cy="14" r="4" /><path d="M12 4v4M5 14h.01M19 14h.01M6.3 9.3l1.4 1.4M17.7 9.3l-1.4 1.4" /><line x1="2" y1="20" x2="22" y2="20" /><polyline points="9 11 12 14 15 11" /></>} />;
const IcUtensils = (p) => <Icon {...p} d={<><path d="M6 3v7a2 2 0 0 0 4 0V3" /><path d="M8 3v18" /><path d="M16 3c-1.5 0-2.5 1.8-2.5 5s1 5 2.5 5v8" /></>} />;
const IcBriefcase = (p) => <Icon {...p} d={<><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" /></>} />;
const IcLuggage = (p) => <Icon {...p} d={<><rect x="4" y="8" width="16" height="12" rx="2" /><path d="M9 8V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v3" /><circle cx="9" cy="21" r="1" fill="currentColor" stroke="none" /><circle cx="15" cy="21" r="1" fill="currentColor" stroke="none" /></>} />;
const IcInfo = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>} />;
const IcX = (p) => <Icon {...p} d={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} />;
const IcCheck = (p) => <Icon {...p} strokeWidth={p.strokeWidth || 2.5} d={<polyline points="20 6 9 17 4 12" />} />;
const IcTicket = (p) => <Icon {...p} d={<><path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4Z" /><line x1="13" y1="7" x2="13" y2="17" strokeDasharray="2.2 2.2" /></>} />;
const IcPlaneFill = ({ size = 14, color = "currentColor", style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0, ...style }}>
    <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
  </svg>
);

// ─── Airport list ─────────────────────────────────────────────────
const AIRPORTS = [
  { code: "DEL", city: "New Delhi", name: "Indira Gandhi Intl" },
  { code: "BOM", city: "Mumbai", name: "Chhatrapati Shivaji Intl" },
  { code: "BLR", city: "Bengaluru", name: "Kempegowda Intl" },
  { code: "MAA", city: "Chennai", name: "Chennai International" },
  { code: "HYD", city: "Hyderabad", name: "Rajiv Gandhi Intl" },
  { code: "CCU", city: "Kolkata", name: "Netaji Subhas Chandra Bose" },
  { code: "AMD", city: "Ahmedabad", name: "Sardar Vallabhbhai Patel" },
  { code: "COK", city: "Kochi", name: "Cochin International" },
  { code: "GOI", city: "Goa", name: "Goa Intl (Dabolim)" },
  { code: "JAI", city: "Jaipur", name: "Jaipur International" },
  { code: "PNQ", city: "Pune", name: "Pune International" },
  { code: "LKO", city: "Lucknow", name: "Chaudhary Charan Singh" },
  { code: "ATQ", city: "Amritsar", name: "Sri Guru Ram Dass Jee Intl" },
  { code: "IXC", city: "Chandigarh", name: "Chandigarh International" },
  { code: "SXR", city: "Srinagar", name: "Sheikh ul-Alam International" },
  { code: "GAU", city: "Guwahati", name: "Lokpriya Gopinath Bordoloi" },
  { code: "BBI", city: "Bhubaneswar", name: "Biju Patnaik International" },
  { code: "VTZ", city: "Visakhapatnam", name: "Visakhapatnam Airport" },
  { code: "NAG", city: "Nagpur", name: "Dr. Babasaheb Ambedkar Intl" },
  { code: "IXB", city: "Siliguri", name: "Bagdogra Airport" },
  { code: "TRV", city: "Thiruvananthapuram", name: "Trivandrum International" },
  { code: "CJB", city: "Coimbatore", name: "Coimbatore International" },
  { code: "IXM", city: "Madurai", name: "Madurai Airport" },
  { code: "IXZ", city: "Port Blair", name: "Veer Savarkar International" },
  { code: "PAT", city: "Patna", name: "Jay Prakash Narayan Intl" },
  { code: "RPR", city: "Raipur", name: "Swami Vivekananda Airport" },
  { code: "BHO", city: "Bhopal", name: "Raja Bhoj Airport" },
  { code: "IDR", city: "Indore", name: "Devi Ahilya Bai Holkar" },
  { code: "VNS", city: "Varanasi", name: "Lal Bahadur Shastri" },
  { code: "IXR", city: "Ranchi", name: "Birsa Munda Airport" },
  { code: "JDH", city: "Jodhpur", name: "Jodhpur Airport" },
  { code: "UDR", city: "Udaipur", name: "Maharana Pratap Airport" },
  { code: "IMF", city: "Imphal", name: "Bir Tikendrajit International" },
  { code: "IXE", city: "Mangaluru", name: "Mangalore International" },
  { code: "DIB", city: "Dibrugarh", name: "Dibrugarh Airport" },
];

// Foreign-only airports — combined with AIRPORTS (below) into INTERNATIONAL_AIRPORTS, since an
// international trip still starts or ends at an Indian city on one side of the route.
const FOREIGN_AIRPORTS = [
  { code: "DXB", city: "Dubai", name: "Dubai International" },
  { code: "AUH", city: "Abu Dhabi", name: "Abu Dhabi International" },
  { code: "SHJ", city: "Sharjah", name: "Sharjah International" },
  { code: "DOH", city: "Doha", name: "Hamad International" },
  { code: "SIN", city: "Singapore", name: "Singapore Changi" },
  { code: "KUL", city: "Kuala Lumpur", name: "Kuala Lumpur Intl" },
  { code: "BKK", city: "Bangkok", name: "Suvarnabhumi Airport" },
  { code: "CMB", city: "Colombo", name: "Bandaranaike Intl" },
  { code: "KTM", city: "Kathmandu", name: "Tribhuvan Intl" },
  { code: "DAC", city: "Dhaka", name: "Hazrat Shahjalal Intl" },
  { code: "LHR", city: "London", name: "Heathrow Airport" },
  { code: "JFK", city: "New York", name: "John F. Kennedy Intl" },
  { code: "HKG", city: "Hong Kong", name: "Hong Kong International" },
  { code: "SYD", city: "Sydney", name: "Sydney Kingsford Smith" },
  { code: "MEL", city: "Melbourne", name: "Melbourne Airport" },
  { code: "MCT", city: "Muscat", name: "Muscat International" },
  { code: "KWI", city: "Kuwait City", name: "Kuwait International" },
  { code: "RUH", city: "Riyadh", name: "King Khalid International" },
  { code: "JED", city: "Jeddah", name: "King Abdulaziz Intl" },
  { code: "BAH", city: "Manama", name: "Bahrain International" },
  { code: "IND", city: "Indianapolis", name: "Indianapolis International" },
];

// Shown instead of AIRPORTS when the Domestic/International toggle is set to International —
// foreign airports only.
const INTERNATIONAL_AIRPORTS = FOREIGN_AIRPORTS;

const CLASS_OPTS = ["Economy", "Business", "First Class", "Premium Economy"];
// Nextra's own prefclass codes for its /search request — NOT documented anywhere in this
// codebase (no Nextra API doc on file), so these are the standard GDS single-letter codes
// (E=Economy, B=Business, F=First, W=Premium Economy). Verify against a real Business/First/
// Premium Economy search before trusting the results — if Nextra uses different codes this
// will silently search Economy-equivalent or return no fares instead of erroring loudly.
const NEXTRA_CLASS_CODES = ["E", "B", "F", "W"];

// YYYY-MM-DD → MM/DD/YYYY for WeFlyGo API
const toApiDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${m}/${d}/${y}`;
};

const fmtTime = (dt) => {
  if (!dt) return "--";
  // ISO ("2026-08-08T01:30:00"): time starts right after the T. Non-ISO
  // ("08/08/2026 01:30", exactly 16 chars) used to fall through the old
  // length>16 check and render as-is — showing the whole date+time as the
  // "time". Split on whitespace instead so either shape works.
  if (dt.includes("T")) return dt.slice(11, 16);
  const parts = dt.trim().split(/\s+/);
  return parts.length > 1 ? parts[1].slice(0, 5) : dt;
};

const fmtCardDate = (dt) => {
  if (!dt) return "";
  const d = new Date(dt);
  return isNaN(d) ? "" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const totalFareAmount = (fare) =>
  (fare?.FareDetails || []).reduce((s, fd) => s + (fd.Total_Amount || 0), 0);

// ─── Nextra response normalization ─────────────────────────────────
// Nextra's request/response shapes are structurally unrelated to WeFlyGo's — flatten
// its result into the exact TripDetails/Flights/Segments/Fares shape the rest of this
// file (FlightCard, filters, sorting) already expects, so no rendering code needs to
// know which provider actually ran. Confirmed against a live Nextra UAT /search call:
// depdate/arrdate are "YYYY-MM-DD", deptime/arrtime are 4-digit "HHMM" (no colon).
const nextraDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr || timeStr.length < 4) return null;
  return `${dateStr}T${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}:00`;
};

const nextraDurationLabel = (mins) => {
  if (mins === null || mins === undefined) return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const nextraBaggageText = (baginfo, kind) => {
  const b = (baginfo || [])[0];
  if (!b) return "";
  if (kind === "checkin") return b.checkin_baggageweight || (b.checkin_baggage ? `${b.checkin_baggage} ${b.checkin_unit || "KG"}` : "");
  return b.cabin_baggage ? `${b.cabin_baggage} ${b.cabin_unit || "KG"}` : "";
};

// carrier ("6ES") is the supplier's own code — validatingcarrier ("6E") is the real
// IATA code the rest of this file's airline logo/lookup logic expects.
const normalizeNextraFlight = (rf) => {
  const legs = rf.flightlegs || [];
  const first = legs[0] || {};
  return {
    Flight_Key: rf.nextraflightkey,
    Airline_Code: first.validatingcarrier || first.carrier || "",
    Segments: legs.map((leg) => ({
      Airline_Code: leg.validatingcarrier || leg.carrier,
      Airline_Name: leg.validatingcarriername || leg.carriername || rf.supplier,
      Flight_Number: leg.flightnumber,
      Aircraft_Type: leg.equipment,
      Origin: leg.origin,
      Destination: leg.destination,
      Departure_DateTime: nextraDateTime(leg.depdate, leg.deptime),
      Arrival_DateTime: nextraDateTime(leg.arrdate, leg.arrtime),
      Duration: nextraDurationLabel(leg.journeyduration),
    })),
    Fares: [{
      Fare_Id: rf.nextraflightkey,
      Refundable: rf.isrefundable === "true",
      Seats_Available: first.numseatsavailable,
      FareDetails: [{
        Total_Amount: rf.flightfare?.totalnet || 0,
        Free_Baggage: {
          Check_In_Baggage: nextraBaggageText(first.baginfo, "checkin"),
          Hand_Baggage: nextraBaggageText(first.baginfo, "cabin"),
        },
      }],
    }],
  };
};

const normalizeNextraResults = (res) => {
  const journeys = res?.flightsearchresponse?.flightjourneys || [];
  return journeys.map((journey) => ({
    Flights: (journey.flightoptions || []).flatMap((opt) =>
      (opt.recommendedflight || []).map(normalizeNextraFlight)
    ),
  }));
};

// ─── Filter helpers ───────────────────────────────────────────────
const STOP_LABELS = { 0: "Direct", 1: "1 Stop", 2: "2+ Stops" };
const stopsBucket = (segs) => Math.min((segs?.length || 1) - 1, 2);

const TIME_BUCKETS = [
  { key: "early", label: "Before 6 AM", test: (h) => h < 6 },
  { key: "morning", label: "6 AM – 12 PM", test: (h) => h >= 6 && h < 12 },
  { key: "afternoon", label: "12 PM – 6 PM", test: (h) => h >= 12 && h < 18 },
  { key: "evening", label: "After 6 PM", test: (h) => h >= 18 },
];
const departureBucket = (dt) => {
  if (!dt) return null;
  const h = Number(fmtTime(dt).slice(0, 2));
  return Number.isNaN(h) ? null : TIME_BUCKETS.find((b) => b.test(h))?.key || null;
};

const durationMinutes = (flight) => {
  const segs = flight.Segments || [];
  const dep = segs[0]?.Departure_DateTime;
  const arr = segs[segs.length - 1]?.Arrival_DateTime;
  if (!dep || !arr) return Infinity;
  const diff = (new Date(arr) - new Date(dep)) / 60000;
  return diff > 0 ? diff : Infinity;
};

const emptyFilters = () => ({
  stops: new Set(), airlines: new Set(), departure: new Set(),
  maxPrice: null, refundableOnly: false,
});

// ─── Airport dropdown ─────────────────────────────────────────────
const AirportInput = ({ label, value, onChange, airports = AIRPORTS }) => {
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const filtered = airports.filter(
    (a) =>
      a.code.includes(q.toUpperCase()) ||
      a.city.toLowerCase().includes(q.toLowerCase())
  ).slice(0, 8);
  // Once a real airport is picked, show its city under the code inside the
  // same box — matches how a chosen value reads on a real booking site,
  // rather than leaving the code as the only information on screen.
  const matched = airports.find((a) => a.code === (q || "").toUpperCase());

  const pick = (a) => {
    setQ(a.code);
    onChange(a.code);
    setOpen(false);
  };

  return (
    <div style={{ position: "relative" }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </label>
      <div style={{ border: "1.5px solid #D1D5DB", borderRadius: 10, padding: "6px 14px", background: "#fff", boxSizing: "border-box" }}>
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); onChange(e.target.value.toUpperCase()); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder="City or code (e.g. DEL)"
          style={{ width: "100%", border: "none", outline: "none", padding: 0, fontSize: 15, fontWeight: 700, color: "#111827", background: "transparent", boxSizing: "border-box", fontFamily: "inherit" }}
        />
        <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1, minHeight: 14 }}>
          {matched ? matched.city : " "}
        </div>
      </div>
      {open && filtered.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200, background: "#fff", border: "1px solid #E5E7EB", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 220, overflowY: "auto" }}>
          {filtered.map((a) => (
            <div
              key={a.code}
              onMouseDown={() => pick(a)}
              style={{ padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#F0F9FF")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
            >
              <div>
                <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>{a.code}</span>
                <span style={{ fontSize: 12, color: "#6B7280", marginLeft: 8 }}>{a.city}</span>
              </div>
              <span style={{ fontSize: 11, color: "#9CA3AF" }}>{a.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Counter component ────────────────────────────────────────────
const Counter = ({ label, value, min, max, onChange }) => (
  <div>
    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</label>
    <div style={{ display: "flex", alignItems: "center", border: "1.5px solid #D1D5DB", borderRadius: 10, height: 44, overflow: "hidden" }}>
      <button
        onClick={() => onChange(Math.max(min, value - 1))}
        style={{ width: 38, height: "100%", border: "none", background: "#F9FAFB", cursor: "pointer", fontSize: 18, color: "#374151", fontWeight: 700 }}
      >−</button>
      <span style={{ flex: 1, textAlign: "center", fontSize: 16, fontWeight: 700, color: "#111827" }}>{value}</span>
      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        style={{ width: 38, height: "100%", border: "none", background: "#F9FAFB", cursor: "pointer", fontSize: 18, color: "#374151", fontWeight: 700 }}
      >+</button>
    </div>
  </div>
);

// ─── Flight result card ───────────────────────────────────────────
// IATA (2-letter, as returned by the booking API) → ICAO (3-letter, the
// filename convention used by the jxck-s/airline-logos asset repo).
const IATA_TO_ICAO = {
  "6E": "IGO", AI: "AIC", UK: "VTI", SG: "SEJ", G8: "GOW", I5: "AXB", IX: "AXB",
  QP: "AKJ", "9I": "LLR",
  EK: "UAE", QR: "QTR", SQ: "SIA", TG: "THA", MH: "MAS", CX: "CPA", BA: "BAW",
  LH: "DLH", AF: "AFR", KL: "KLM", TK: "THY", EY: "ETD", SV: "SVA", UL: "ALK",
  OS: "AUA", VN: "HVN", JL: "JAL", NH: "ANA", KE: "KAL", CI: "CAL", BR: "EVA",
  PR: "PAL", GA: "GIA", VA: "VOZ", QF: "QFA", NZ: "ANZ", EI: "EIN", LX: "SWR",
  IB: "IBE", AZ: "ITY", SU: "AFL", FZ: "UAE", ET: "ETH",
};
const airlineLogoUrl = (iataCode) => {
  const icao = IATA_TO_ICAO[(iataCode || "").toUpperCase()];
  return icao ? `https://raw.githubusercontent.com/Jxck-S/airline-logos/main/flightaware_logos/${icao}.png` : null;
};

// Deterministic accent per airline so results stay visually distinguishable
// when no logo is available.
const AIRLINE_PALETTE = [
  { bg: "#EFF6FF", fg: "#3B82F6" },
  { bg: "#F0FDF4", fg: "#059669" },
  { bg: "#FDF4FF", fg: "#A21CAF" },
  { bg: "#FFF7ED", fg: "#C2410C" },
  { bg: "#FEF2F2", fg: "#DC2626" },
  { bg: "#ECFEFF", fg: "#0891B2" },
];
const airlineAccent = (key) => {
  const s = (key || "").toUpperCase();
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AIRLINE_PALETTE[hash % AIRLINE_PALETTE.length];
};

const FlightCard = ({ flight, selected, onSelect, onViewFareRules, provider }) => {
  const [hover, setHover] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const fare = (flight.Fares || [])[0];
  const total = totalFareAmount(fare);
  const segs = flight.Segments || [];
  const first = segs[0];
  const last = segs[segs.length - 1];
  const stops = segs.length - 1;
  const bg0 = fare?.FareDetails?.[0];
  const baggage = bg0?.Free_Baggage;

  const airlineCode = flight.Airline_Code || first?.Airline_Code || "";
  const airlineName = first?.Airline_Name || airlineCode || "Airline";
  const initials = (airlineCode || airlineName).replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "FL";
  const accent = airlineAccent(airlineCode || airlineName);
  const logoUrl = airlineLogoUrl(airlineCode);
  // flight.Flight_Numbers is a raw WeFlyGo field that, for connecting itineraries, can come back
  // as more than comma-joined flight numbers (a segment's date/time can be jammed in too) — build
  // the display from each segment's own clean Flight_Number instead of parsing that string.
  const flightNumbers = segs.map((s) => s.Flight_Number).filter(Boolean).join(" · ");

  const select = () => onSelect(flight);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={select}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); select(); } }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        border: selected ? `2px solid ${AIR.navy}` : "1.5px solid #E5E7EB",
        borderRadius: 16, padding: "16px 20px", marginBottom: 14, cursor: "pointer",
        background: selected ? AIR.paper : "#fff",
        boxShadow: selected
          ? "0 0 0 3px rgba(11,26,53,0.10), 0 10px 24px rgba(11,26,53,0.08)"
          : hover ? "0 6px 18px rgba(15,23,42,0.09)" : "0 1px 3px rgba(0,0,0,0.04)",
        transform: hover && !selected ? "translateY(-1px)" : "none",
        transition: "box-shadow 0.15s, transform 0.15s, border-color 0.15s, background 0.15s",
        outline: "none",
      }}
    >
      <div className="air-card-row" style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {/* Airline */}
        <div className="air-card-airline" style={{ display: "flex", flexDirection: "column", gap: 8, width: 158, flexShrink: 0, minWidth: 0 }}>
          <div className="air-card-airline-main" style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            {logoUrl && !logoFailed ? (
              <img
                src={logoUrl}
                alt={airlineName}
                onError={() => setLogoFailed(true)}
                style={{ width: 44, height: 44, borderRadius: 12, objectFit: "contain", background: accent.bg, border: "1px solid #F1F5F9", padding: 6, flexShrink: 0, boxSizing: "border-box" }}
              />
            ) : (
              <div style={{
                width: 44, height: 44, borderRadius: 12, background: accent.bg, color: accent.fg,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, flexShrink: 0,
              }}>
                {initials}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#1E3A5F", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {airlineName}
              </div>
              <div style={{ marginTop: 1 }}>
                <Readout size={11} weight={500} color="#9CA3AF">
                  {flightNumbers}
                </Readout>
              </div>
              {first?.Aircraft_Type && (
                <div style={{ fontSize: 10, color: "#CBD5E1", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{first.Aircraft_Type}</div>
              )}
            </div>
          </div>

          {(baggage?.Check_In_Baggage || baggage?.Hand_Baggage || fare?.Food_onboard) && (
            <div className="air-card-airline-icons" style={{ display: "flex", gap: 6 }}>
              {baggage?.Check_In_Baggage && (
                <span title={`Check-in baggage: ${baggage.Check_In_Baggage}`} style={{ width: 26, height: 26, borderRadius: 8, background: "#F1F5F9", color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IcBriefcase size={13} />
                </span>
              )}
              {baggage?.Hand_Baggage && (
                <span title={`Cabin baggage: ${baggage.Hand_Baggage}`} style={{ width: 26, height: 26, borderRadius: 8, background: "#F1F5F9", color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IcLuggage size={13} />
                </span>
              )}
              {fare?.Food_onboard && (
                <span title={fare.Food_onboard === "F" ? "Meal included" : "Meal available (paid)"} style={{ width: 26, height: 26, borderRadius: 8, background: "#F1F5F9", color: "#64748B", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IcUtensils size={13} />
                </span>
              )}
            </div>
          )}
        </div>

        {/* Route */}
        <div className="air-card-route" style={{ flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div style={{ textAlign: "center" }}>
            <Readout size={21} weight={700} color="#111827">{fmtTime(first?.Departure_DateTime)}</Readout>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{fmtCardDate(first?.Departure_DateTime)}</div>
            <div style={{ marginTop: 1 }}><Readout size={12} weight={700} color="#6B7280">{first?.Origin}</Readout></div>
          </div>
          <div style={{ flex: 1, textAlign: "center", minWidth: 70 }}>
            <div style={{ fontSize: 10, color: "#9CA3AF", marginBottom: 5 }}>
              {first?.Duration || segs.reduce((s, sg) => s + " " + (sg.Duration || ""), "").trim()}
            </div>
            <div style={{ position: "relative", display: "flex", alignItems: "center", height: 15 }}>
              <div style={{ flex: 1, borderTop: "1.5px dashed #CBD5E1" }} />
              <span style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", background: selected ? "#F7F9FC" : "#fff", color: "#94A3B8", display: "flex", padding: "0 3px" }}>
                <IcPlaneFill size={13} color="#94A3B8" />
              </span>
            </div>
            <div style={{ marginTop: 5 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8,
                background: stops === 0 ? "#D1FAE5" : "#FEF3C7",
                color: stops === 0 ? "#059669" : "#B45309",
              }}>
                {stops === 0 ? "Direct" : `${stops} stop${stops > 1 ? "s" : ""}`}
              </span>
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <Readout size={21} weight={700} color="#111827">{fmtTime(last?.Arrival_DateTime)}</Readout>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{fmtCardDate(last?.Arrival_DateTime)}</div>
            <div style={{ marginTop: 1 }}><Readout size={12} weight={700} color="#6B7280">{last?.Destination}</Readout></div>
          </div>
        </div>

        {/* Divider */}
        <div className="air-card-divider" style={{ width: 1, alignSelf: "stretch", borderLeft: "1.5px dashed #E2E8F0", flexShrink: 0 }} />

        {/* Price + action */}
        <div className="air-card-price" style={{ textAlign: "right", minWidth: 148, flexShrink: 0, paddingLeft: 4 }}>
          <Readout size={23} weight={700} color={AIR.sky} tracking="-0.01em">₹{total.toLocaleString("en-IN")}</Readout>
          <div style={{ fontSize: 10, color: "#9CA3AF" }}>{provider === "nextra" ? "total price" : "per pax"}</div>
          {fare?.Refundable !== undefined && (
            <div style={{
              display: "inline-block", marginTop: 4, fontSize: 10, fontWeight: 700,
              color: fare.Refundable ? "#059669" : "#DC2626",
              background: fare.Refundable ? "#D1FAE5" : "#FEE2E2",
              borderRadius: 6, padding: "2px 7px",
            }}>
              {fare.Refundable ? "Refundable" : "Non-refundable"}
            </div>
          )}
          {selected && (
            <button
              onClick={(e) => { e.stopPropagation(); select(); }}
              title="Remove selection"
              style={{
                marginTop: 9, display: "inline-flex", alignItems: "center", gap: 6,
                background: AIR.navy, color: "#fff", border: "none", cursor: "pointer",
                borderRadius: 9, padding: "8px 15px", fontSize: 12, fontWeight: 700,
              }}
            >
              Selected <IcCheck size={12} />
              <span style={{ width: 1, alignSelf: "stretch", background: "rgba(255,255,255,0.3)" }} />
              <IcX size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Footer strip */}
      {(fare?.Seats_Available || onViewFareRules) && (
        <div style={{ marginTop: 14, background: "#F1F5F9", borderRadius: 10, padding: "9px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          <span>
            {fare?.Seats_Available && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#DC2626" }}>
                <IcLuggage size={13} /> <Readout size={12} weight={700} color="#DC2626">{fare.Seats_Available} seat{Number(fare.Seats_Available) > 1 ? "s" : ""} left</Readout>
              </span>
            )}
          </span>
          {onViewFareRules && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewFareRules(flight, fare); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", color: AIR.sky, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: 0 }}
            >
              View fare rules <IcInfo size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Filter bar ───────────────────────────────────────────────────
const STOP_ICONS = { 0: IcPlaneFill, 1: IcMapPin, 2: IcLayers };
const TIME_ICONS = { early: IcMoon, morning: IcSunrise, afternoon: IcSun, evening: IcSunset };

const FilterChip = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
      border: "1.5px solid", cursor: "pointer", whiteSpace: "nowrap",
      borderColor: active ? "#1E3A5F" : "#E5E7EB",
      background: active ? "#1E3A5F" : "#fff",
      color: active ? "#fff" : "#6B7280",
      transition: "all 0.15s",
    }}
  >
    {label}
  </button>
);

const FilterTile = ({ icon: IconComp, label, active, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 7,
      padding: "9px 10px", borderRadius: 10, textAlign: "left",
      border: `1.5px solid ${active ? "#3B82F6" : "#E5E7EB"}`,
      background: active ? "#EFF6FF" : "#fff",
      color: active ? "#3B82F6" : "#475569",
      fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s",
    }}
  >
    <IconComp size={14} />
    <span>{label}</span>
  </button>
);

const FilterRow = ({ label, children, first }) => (
  <div style={{ paddingTop: first ? 0 : 14, marginTop: first ? 0 : 14, borderTop: first ? "none" : "1px solid #F1F5F9" }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
      {label}
    </div>
    {children}
  </div>
);

const FilterBar = ({
  airlineOptions, priceMin, priceMax, filters,
  onToggleStop, onToggleAirline, onToggleTime, onPriceChange, onToggleRefundable,
  sortBy, onSortChange, activeCount, onClear, resultCount, totalCount,
}) => {
  const pricePct = priceMax > priceMin
    ? Math.round((((filters.maxPrice ?? priceMax) - priceMin) / (priceMax - priceMin)) * 100)
    : 100;

  return (
    <div
      style={{ background: "#fff", border: "1.5px solid #E5E7EB", borderRadius: 16, padding: "18px 16px" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: "#1E3A5F" }}>Filters</span>
        <button
          onClick={activeCount > 0 ? onClear : undefined}
          title={activeCount > 0 ? "Clear all filters" : "Filters"}
          style={{
            background: "none", border: "none", padding: 4, borderRadius: 6, display: "flex",
            color: activeCount > 0 ? "#3B82F6" : "#CBD5E1",
            cursor: activeCount > 0 ? "pointer" : "default",
          }}
        >
          <IcSliders size={17} />
        </button>
      </div>
      <div style={{ marginBottom: 14 }}>
        <Readout size={12} weight={500} color="#9CA3AF">{resultCount} of {totalCount} flights</Readout>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
          Sort by
        </label>
        <select
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          style={{ width: "100%", height: 38, borderRadius: 8, border: "1.5px solid #D1D5DB", padding: "0 10px", fontSize: 12, fontWeight: 600, color: "#111827", background: "#fff", outline: "none", cursor: "pointer", boxSizing: "border-box" }}
        >
          <option value="price">Cheapest first</option>
          <option value="duration">Shortest duration</option>
          <option value="departure">Earliest departure</option>
        </select>
      </div>

      <FilterRow label="Stops" first>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <FilterTile icon={STOP_ICONS[0]} label={STOP_LABELS[0]} active={filters.stops.has(0)} onClick={() => onToggleStop(0)} />
          <FilterTile icon={STOP_ICONS[1]} label={STOP_LABELS[1]} active={filters.stops.has(1)} onClick={() => onToggleStop(1)} />
          <div style={{ gridColumn: "1 / -1" }}>
            <FilterTile icon={STOP_ICONS[2]} label={STOP_LABELS[2]} active={filters.stops.has(2)} onClick={() => onToggleStop(2)} />
          </div>
        </div>
      </FilterRow>

      <FilterRow label="Departure time">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {TIME_BUCKETS.map((b) => (
            <FilterTile key={b.key} icon={TIME_ICONS[b.key]} label={b.label} active={filters.departure.has(b.key)} onClick={() => onToggleTime(b.key)} />
          ))}
        </div>
      </FilterRow>

      {airlineOptions.length > 1 && (
        <FilterRow label="Airline">
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {airlineOptions.map((a) => (
              <FilterChip key={a} label={a} active={filters.airlines.has(a)} onClick={() => onToggleAirline(a)} />
            ))}
          </div>
        </FilterRow>
      )}

      {priceMax > priceMin && (
        <div style={{ paddingTop: 14, marginTop: 14, borderTop: "1px solid #F1F5F9" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.6 }}>Max price</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: "#1E3A5F" }}>₹{(filters.maxPrice ?? priceMax).toLocaleString("en-IN")}</span>
          </div>
          <input
            type="range" min={priceMin} max={priceMax}
            step={Math.max(1, Math.round((priceMax - priceMin) / 100))}
            value={filters.maxPrice ?? priceMax}
            onChange={(e) => onPriceChange(Number(e.target.value))}
            style={{
              width: "100%", height: 5, borderRadius: 3, appearance: "none", cursor: "pointer", accentColor: "#3B82F6", outline: "none",
              background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${pricePct}%, #E2E8F0 ${pricePct}%, #E2E8F0 100%)`,
            }}
          />
        </div>
      )}

      <div style={{ paddingTop: 14, marginTop: 14, borderTop: "1px solid #F1F5F9" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
          <input
            type="checkbox" checked={filters.refundableOnly} onChange={onToggleRefundable}
            style={{ width: 16, height: 16, accentColor: "#1E3A5F", cursor: "pointer" }}
          />
          <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Refundable only</span>
        </label>
      </div>
    </div>
  );
};

// ─── Fare rules modal ───────────────────────────────────────────────
// GDS fare-rule text is print-oriented plain text wrapped in heavy inline-styled HTML
// (ALL CAPS, "---" section dividers, unfilled "&&&TOKEN&&&" placeholders). Strip the HTML
// and re-flow it into clean paragraphs instead of rendering the airline's raw markup.
const cleanFareRuleText = (html) => {
  if (!html) return [];
  const doc = new DOMParser().parseFromString(html, "text/html");
  const raw = doc.body.textContent || "";
  const withoutPlaceholders = raw.replace(/&&&(.*?)&&&/g, "$1");
  return withoutPlaceholders
    .split(/-{3,}/g)
    .map((s) => s.replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

// This page only ever searches via Nextra (provider is hardcoded above) — fareId here is
// always rf.nextraflightkey, an opaque per-flight token straight from Nextra's own search
// response, so it goes to Nextra's /farerule as-is, not WeFlyGo's /fare-rule (a different
// vendor's endpoint this modal was calling before, which always failed since Nextra's flight
// key means nothing to WeFlyGo).
const FareRuleModal = ({ fareId, domInt, title, onClose }) => {
  const { callApi, loading } = useApi("/air/nextra/farerule", "POST");
  const [rules, setRules] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setRules(null);
    setError("");
    callApi({ selectedFlight: fareId, domInt })
      .then((res) => {
        if (cancelled) return;
        const nextraError = apiErrorMessage(res);
        if (nextraError) { setError(nextraError); return; }
        const rule = res?.ApiStatus?.Result?.Rule;
        if (!rule) {
          setError("Fare rules aren't available for this flight.");
          return;
        }
        // Adapted to this modal's existing render shape (an array of {FareRuleName,
        // FareRuleDesc} sections) — Nextra returns one combined HTML block instead.
        setRules([{ FareRuleName: null, FareRuleDesc: rule }]);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message || e.message || "Could not load fare rules.");
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fareId, domInt]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, maxWidth: 640, width: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #E5E7EB" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#1E3A5F" }}>Fare Rules</div>
            {title && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>{title}</div>}
          </div>
          <button onClick={onClose} style={{ background: "#F3F4F6", border: "none", borderRadius: 8, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", color: "#6B7280", cursor: "pointer", flexShrink: 0 }}>
            <IcX size={15} />
          </button>
        </div>
        <div style={{ padding: "20px 24px", overflowY: "auto" }}>
          {loading && <div style={{ color: "#9CA3AF", textAlign: "center", padding: "20px 0", fontSize: 13 }}>Loading fare rules…</div>}
          {error && !loading && <div style={{ color: "#DC2626", fontSize: 13 }}>{error}</div>}
          {!loading && !error && rules?.map((r, i) => {
            const paragraphs = cleanFareRuleText(r.FareRuleDesc);
            return (
              <div key={i} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: i < rules.length - 1 ? "1px solid #F1F5F9" : "none" }}>
                {r.FareRuleName && (
                  <div style={{
                    display: "inline-block", fontSize: 11, fontWeight: 800, color: "#1E3A5F",
                    background: "#EFF6FF", padding: "4px 10px", borderRadius: 6,
                    letterSpacing: "0.05em", marginBottom: 14,
                  }}>
                    {r.FareRuleName}
                  </div>
                )}
                {paragraphs.length === 0 ? (
                  <div style={{ color: "#9CA3AF", fontSize: 13, fontStyle: "italic" }}>No details provided.</div>
                ) : (
                  paragraphs.map((p, pi) => (
                    <p key={pi} style={{ margin: "0 0 12px", fontSize: 13, lineHeight: 1.75, color: "#374151" }}>
                      {p}
                    </p>
                  ))
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────
const FlightSearch = () => {
  const navigate = useNavigate();
  // Air_Reprice's Customer_Mobile falls back to "the agency's registered mobile number" per
  // WeFlyGo docs when no customer contact is known yet (that's collected later, on the booking
  // form) — mobile_number is the merchant's actual registered phone, not username (their login/mid).
  const { mobile_number: merchantMobile } = useSelector((s) => s.auth.employeeDetails) || {};
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    tripType: 0, travelType: 0,
    from: "", to: "",
    travelDate: today, returnDate: today,
    adults: 1, children: 0, infants: 0,
    classOfTravel: 0,
  });
  const [error, setError] = useState("");
  const [step, setStep] = useState("form"); // "form" | "results"
  // Air1 (WeFlyGo) and Air2 (Nextra) toggle is hidden for now — Nextra is the only provider used.
  const provider = "nextra";
  const [searchKey, setSearchKey] = useState("");
  const [tripResults, setTripResults] = useState([]);
  const [selectedFlights, setSelectedFlights] = useState({});
  const [filters, setFilters] = useState(emptyFilters);
  const [sortBy, setSortBy] = useState("price");
  const [fareRuleTarget, setFareRuleTarget] = useState(null);
  // Mobile only — desktop always shows the sidebar regardless of this (see the
  // .air-filter-sidebar CSS, which only reacts to this class inside the mobile media query).
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Mobile only — the "modify search" card collapses into a one-line summary by default
  // (see .air-search-summary/.air-search-body CSS, mobile media query only).
  const [searchOpen, setSearchOpen] = useState(false);

  const { callApi: searchApi, loading: searchingWeFlyGo } = useApi("/air/search", "POST");
  const { callApi: nextraSearchApi, loading: searchingNextra } = useApi("/air/nextra/search", "POST");
  const { callApi: repriceApi, loading: repricingWeFlyGo } = useApi("/air/reprice", "POST");
  const { callApi: nextraRepriceApi, loading: repricingNextra } = useApi("/air/nextra/reprice", "POST");
  const searching = provider === "nextra" ? searchingNextra : searchingWeFlyGo;
  const repricing = provider === "nextra" ? repricingNextra : repricingWeFlyGo;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // ─── Filtering / sorting ────────────────────────────────────────
  const allFlights = useMemo(() => tripResults.flatMap((t) => t.Flights || []), [tripResults]);

  const airlineOptions = useMemo(() => {
    const s = new Set();
    allFlights.forEach((f) => {
      const name = f.Segments?.[0]?.Airline_Name || f.Airline_Code;
      if (name) s.add(name);
    });
    return Array.from(s).sort();
  }, [allFlights]);

  const priceBounds = useMemo(() => {
    const prices = allFlights.map((f) => totalFareAmount((f.Fares || [])[0])).filter((p) => p > 0);
    return prices.length ? { min: Math.min(...prices), max: Math.max(...prices) } : { min: 0, max: 0 };
  }, [allFlights]);

  const matchesFilters = (flight) => {
    const segs = flight.Segments || [];
    if (filters.stops.size && !filters.stops.has(stopsBucket(segs))) return false;
    const airline = segs[0]?.Airline_Name || flight.Airline_Code;
    if (filters.airlines.size && !filters.airlines.has(airline)) return false;
    const total = totalFareAmount((flight.Fares || [])[0]);
    if (filters.maxPrice != null && total > filters.maxPrice) return false;
    if (filters.refundableOnly && (flight.Fares || [])[0]?.Refundable !== true) return false;
    if (filters.departure.size) {
      const bucket = departureBucket(segs[0]?.Departure_DateTime);
      if (!bucket || !filters.departure.has(bucket)) return false;
    }
    return true;
  };

  const sortFlights = (flights) => {
    const arr = [...flights];
    if (sortBy === "duration") arr.sort((a, b) => durationMinutes(a) - durationMinutes(b));
    else if (sortBy === "departure") arr.sort((a, b) => new Date(a.Segments?.[0]?.Departure_DateTime) - new Date(b.Segments?.[0]?.Departure_DateTime));
    else arr.sort((a, b) => totalFareAmount((a.Fares || [])[0]) - totalFareAmount((b.Fares || [])[0]));
    return arr;
  };

  const toggleInFilter = (key, value) =>
    setFilters((prev) => {
      const next = new Set(prev[key]);
      next.has(value) ? next.delete(value) : next.add(value);
      return { ...prev, [key]: next };
    });

  const activeFilterCount =
    filters.stops.size + filters.airlines.size + filters.departure.size +
    (filters.refundableOnly ? 1 : 0) +
    (filters.maxPrice != null && filters.maxPrice < priceBounds.max ? 1 : 0);

  const filteredResultCount = tripResults.reduce(
    (sum, t) => sum + (t.Flights || []).filter(matchesFilters).length, 0
  );

  const handleSearch = async () => {
    setError("");
    if (!form.from || form.from.length < 3) return setError("Enter a valid origin airport code");
    if (!form.to || form.to.length < 3) return setError("Enter a valid destination airport code");
    if (form.from.toUpperCase() === form.to.toUpperCase()) return setError("Origin and destination cannot be the same");
    if (!form.travelDate) return setError("Select departure date");

    try {
      if (provider === "nextra") {
        const res = await nextraSearchApi({
          origin: form.from.toUpperCase(),
          destination: form.to.toUpperCase(),
          journeyType: form.tripType === 1 ? "RoundTrip" : "OneWay",
          adultCount: form.adults,
          childCount: form.children,
          infantCount: form.infants,
          numResults: 50,
          onwardDate: form.travelDate,
          returnDate: form.tripType === 1 ? form.returnDate : null,
          prefCarrier: "ALL",
          prefClass: NEXTRA_CLASS_CODES[form.classOfTravel] || "E",
        });

        const trips = normalizeNextraResults(res);
        if (!trips.some((t) => t.Flights.length)) {
          return setError(res?.flightsearchresponse?.statusmessage || "No flights found. Check your inputs and try again.");
        }
        setSearchKey(res?.flightsearchresponse?.nextrasearchkey || "");
        setTripResults(trips);
        setSelectedFlights({});
        setFilters(emptyFilters());
        setStep("results");
        return;
      }

      const tripInfo = [
        { Origin: form.from.toUpperCase(), Destination: form.to.toUpperCase(), TravelDate: toApiDate(form.travelDate), Trip_Id: 0 },
      ];
      if (form.tripType === 1 && form.returnDate) {
        tripInfo.push({ Origin: form.to.toUpperCase(), Destination: form.from.toUpperCase(), TravelDate: toApiDate(form.returnDate), Trip_Id: 1 });
      }

      const res = await searchApi({
        travelType: form.travelType,
        bookingType: form.tripType,
        tripInfo,
        adultCount: form.adults,
        childCount: form.children,
        infantCount: form.infants,
        classOfTravel: form.classOfTravel,
        sourceType: 0,
        srCitizenSearch: false,
        studentFareSearch: false,
        defenceFareSearch: false,
        airlineCodes: [],
      });

      if (!res?.Search_Key) {
        return setError(apiErrorMessage(res) || "No results. Check your inputs and try again.");
      }
      setSearchKey(res.Search_Key);
      setTripResults(res.TripDetails || []);
      setSelectedFlights({});
      setFilters(emptyFilters());
      setStep("results");
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Search failed. Try again.");
    }
  };

  const canProceed = form.tripType === 0 ? !!selectedFlights[0] : !!selectedFlights[0] && !!selectedFlights[1];

  // WeFlyGo's FareDetails is PER PAX TYPE (PAX_Type: 0=Adult/1=Child/2=Infant, per
  // AirFareDetail.java) — totalFareAmount() above sums every entry, which is fine for relative
  // price sorting/filtering, but summing-then-multiplying-by-headcount here would double-(or
  // worse-)count as soon as pax types are mixed. Nextra's Total_Amount is already the grand
  // total for every passenger searched, so it's used as-is (no further multiplication).
  const totalSelected = Object.values(selectedFlights).reduce((sum, f) => {
    if (provider === "nextra") return sum + totalFareAmount((f.Fares || [])[0]);
    const countByType = [form.adults || 0, form.children || 0, form.infants || 0];
    const fareDetails = f.Fares?.[0]?.FareDetails || [];
    return sum + fareDetails.reduce((s, fd) => s + (fd.Total_Amount || 0) * (countByType[fd.PAX_Type] ?? 0), 0);
  }, 0);

  const handleBook = async () => {
    setError("");

    if (provider === "nextra") {
      const flights = Object.keys(selectedFlights).sort().map((idx) => selectedFlights[idx]);
      if (!flights.length) return;

      const domInt = form.travelType === 0 ? "domestic" : "international";
      try {
        const res = await nextraRepriceApi({
          selectedFlight: flights[0].Flight_Key,
          selectedFlightReturn: flights[1]?.Flight_Key || "",
          domInt,
        });
        const pr = res?.NextraPricingResponseV4;
        if (!pr?.flightjourneys?.length) {
          return setError(pr?.statusmessage || res?.message || "Price check failed. Please re-select.");
        }

        const repricedFlights = pr.flightjourneys
          .map((j) => (j.flightoptions || []).flatMap((o) => (o.recommendedflight || []).map(normalizeNextraFlight))[0])
          .filter(Boolean);
        if (!repricedFlights.length) {
          return setError("Price check failed. Please re-select.");
        }

        navigate("/flight-ssr-seat", {
          state: {
            provider: "nextra",
            searchKey,
            repricedFlights,
            requiredPaxDetails: [],
            paxCounts: { adults: form.adults, children: form.children, infants: form.infants },
            tripType: form.tripType,
            fareChanged: !!pr.pricechanged,
            searchForm: { from: form.from.toUpperCase(), to: form.to.toUpperCase(), travelDate: form.travelDate },
            nextraBookingKey: pr.bookingkey,
            nextraTotalPrice: pr.totalPrice ?? repricedFlights.reduce((s, f) => s + (f.Fares?.[0]?.FareDetails?.[0]?.Total_Amount || 0), 0),
            nextraDomInt: domInt,
          },
        });
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Price check failed. Try again.");
      }
      return;
    }

    const flights = Object.keys(selectedFlights)
      .sort()
      .map((idx) => {
        const f = selectedFlights[idx];
        return { flightKey: f.Flight_Key, fareId: String((f.Fares?.[0]?.Fare_Id) ?? "") };
      });

    try {
      const res = await repriceApi({ searchKey, flights, customerMobile: merchantMobile || "", gstInput: false });
      if (!res?.AirRepriceResponses?.length) {
        return setError(apiErrorMessage(res) || "Price check failed. Please re-select.");
      }

      const repricedFlights = res.AirRepriceResponses.map((r) => r.Flight);
      const fareChanged = repricedFlights.some((f) => f.IsFareChange);

      navigate("/flight-ssr-seat", {
        state: {
          provider: "weflygo",
          searchKey,
          repricedFlights,
          requiredPaxDetails: res.AirRepriceResponses[0].Required_PAX_Details || [],
          paxCounts: { adults: form.adults, children: form.children, infants: form.infants },
          tripType: form.tripType,
          fareChanged,
          searchForm: { from: form.from.toUpperCase(), to: form.to.toUpperCase(), travelDate: form.travelDate },
        },
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Reprice failed. Try again.");
    }
  };

  // ─── Results view ─────────────────────────────────────────────
  if (step === "results") {
    const totalPax = form.adults + form.children + form.infants;
    return (
      <div className={canProceed ? "air-results-page air-has-book-bar" : "air-results-page"} style={{ maxWidth: 1180, margin: "0 auto", padding: "20px 16px", fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif" }}>
        <AirFontImport />
        <div className="air-results-grid" style={{ display: "flex", alignItems: "flex-start", gap: 20 }}>
          {tripResults.length > 0 && (
            <div
              className={`air-filter-sidebar${filtersOpen ? " air-filters-open" : ""}`}
              style={{ flex: "0 0 260px", width: 260, position: "sticky", top: NAVBAR_HEIGHT, alignSelf: "flex-start", maxHeight: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, overflowY: "auto" }}
              onClick={() => filtersOpen && setFiltersOpen(false)}
            >
              {/* Mobile-only bottom-sheet card — the backdrop above stopPropagates here so
                  tapping a filter never closes the sheet, only tapping outside it does. */}
              <div onClick={(e) => e.stopPropagation()}>
                <div className="air-filter-sheet-close" onClick={() => setFiltersOpen(false)}>
                  <IcX size={16} /> Close
                </div>
                <FilterBar
                  airlineOptions={airlineOptions}
                  priceMin={priceBounds.min}
                  priceMax={priceBounds.max}
                  filters={filters}
                  onToggleStop={(s) => toggleInFilter("stops", s)}
                  onToggleAirline={(a) => toggleInFilter("airlines", a)}
                  onToggleTime={(t) => toggleInFilter("departure", t)}
                  onPriceChange={(v) => setFilters((p) => ({ ...p, maxPrice: v }))}
                  onToggleRefundable={() => setFilters((p) => ({ ...p, refundableOnly: !p.refundableOnly }))}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  activeCount={activeFilterCount}
                  onClear={() => setFilters(emptyFilters())}
                  resultCount={filteredResultCount}
                  totalCount={allFlights.length}
                />
              </div>
            </div>
          )}

          <div style={{ flex: "1 1 0%", minWidth: 0 }}>
            <div className={`air-search-card${searchOpen ? " air-search-open" : ""}`} style={{ position: "sticky", top: NAVBAR_HEIGHT, zIndex: 30, background: "#fff", borderRadius: 16, border: "1px solid #E5E7EB", padding: "14px 18px", marginBottom: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>

              {/* Mobile-only collapsed summary — tapping it opens the full form as a dropdown
                  below. Desktop never shows this; the full form is always visible there. */}
              <div className="air-search-summary" onClick={() => setSearchOpen((v) => !v)}>
                <button
                  onClick={(e) => { e.stopPropagation(); setStep("form"); }}
                  style={{ display: "flex", alignItems: "center", background: "#F9FAFB", border: "1.5px solid #E5E7EB", cursor: "pointer", padding: 6, borderRadius: 8, color: "#6B7280", flexShrink: 0 }}
                >
                  <IcArrowLeft size={13} />
                </button>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 700, color: "#1E3A5F", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {form.from.toUpperCase()} → {form.to.toUpperCase()} · {fmtCardDate(form.travelDate)} · {totalPax} pax
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: AIR.sky, display: "flex", alignItems: "center", gap: 3, flexShrink: 0 }}>
                  Modify <span style={{ transition: "transform 0.15s", transform: searchOpen ? "rotate(180deg)" : "none", display: "inline-block" }}>▾</span>
                </span>
              </div>

              <div className="air-search-body">
              {/* Trip type + domestic/international */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <button onClick={() => setStep("form")} style={{ display: "flex", alignItems: "center", gap: 5, background: "#F9FAFB", border: "1.5px solid #E5E7EB", cursor: "pointer", padding: "6px 10px", borderRadius: 8, color: "#6B7280", fontSize: 12, fontWeight: 600 }}>
                  <IcArrowLeft size={13} /> Home
                </button>
                {["One Way", "Round Trip"].map((label, i) => (
                  <button
                    key={i}
                    onClick={() => set("tripType", i)}
                    style={{
                      padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                      border: "1.5px solid", cursor: "pointer", transition: "all 0.15s",
                      borderColor: form.tripType === i ? "#1E3A5F" : "#E5E7EB",
                      background: form.tripType === i ? "#1E3A5F" : "#fff",
                      color: form.tripType === i ? "#fff" : "#6B7280",
                    }}
                  >
                    {label}
                  </button>
                ))}
                <button
                  onClick={() => set("travelType", form.travelType === 0 ? 1 : 0)}
                  style={{
                    marginLeft: "auto", padding: "6px 12px", borderRadius: 20, fontSize: 11,
                    fontWeight: 600, border: "1.5px solid #E5E7EB", cursor: "pointer",
                    background: "#F9FAFB", color: "#374151",
                  }}
                >
                  {form.travelType === 0 ? "🇮🇳 Domestic" : "🌍 International"}
                </button>
              </div>

              {/* From / Swap / To / Dates */}
              <div className="air-compact-fromto-grid" style={{
                display: "grid",
                gridTemplateColumns: form.tripType === 1 ? "1.4fr auto 1.4fr 1fr 1fr" : "1.4fr auto 1.4fr 1fr",
                gap: 10, alignItems: "end", marginBottom: 12,
              }}>
                <AirportInput label="From" value={form.from} onChange={(v) => set("from", v)} airports={form.travelType === 0 ? AIRPORTS : INTERNATIONAL_AIRPORTS} />
                <div className="air-compact-swap" style={{ paddingBottom: 10, fontSize: 18, color: "#9CA3AF", textAlign: "center", cursor: "pointer", userSelect: "none" }}
                  onClick={() => { const t = form.from; set("from", form.to); set("to", t); }}
                  title="Swap"
                >
                  ⇌
                </div>
                <AirportInput label="To" value={form.to} onChange={(v) => set("to", v)} airports={form.travelType === 0 ? AIRPORTS : INTERNATIONAL_AIRPORTS} />
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    Departure
                  </label>
                  <input
                    type="date" value={form.travelDate} min={today}
                    onChange={(e) => set("travelDate", e.target.value)}
                    style={{ width: "100%", height: 40, borderRadius: 10, border: "1.5px solid #D1D5DB", padding: "0 10px", fontSize: 13, color: "#111827", outline: "none", boxSizing: "border-box" }}
                  />
                </div>
                {form.tripType === 1 && (
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                      Return
                    </label>
                    <input
                      type="date" value={form.returnDate} min={form.travelDate}
                      onChange={(e) => set("returnDate", e.target.value)}
                      style={{ width: "100%", height: 40, borderRadius: 10, border: "1.5px solid #D1D5DB", padding: "0 10px", fontSize: 13, color: "#111827", outline: "none", boxSizing: "border-box" }}
                    />
                  </div>
                )}
              </div>

              {/* Passengers + Class + Search */}
              <div className="air-compact-pax-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: 10, alignItems: "end" }}>
                <Counter label="Adults" value={form.adults} min={1} max={9} onChange={(v) => set("adults", v)} />
                <Counter label="Children" value={form.children} min={0} max={8} onChange={(v) => set("children", v)} />
                <Counter label="Infants" value={form.infants} min={0} max={4} onChange={(v) => set("infants", v)} />
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Class</label>
                  <select
                    value={form.classOfTravel}
                    onChange={(e) => set("classOfTravel", Number(e.target.value))}
                    style={{ width: "100%", height: 44, borderRadius: 10, border: "1.5px solid #D1D5DB", padding: "0 10px", fontSize: 12, color: "#111827", background: "#fff", outline: "none", boxSizing: "border-box" }}
                  >
                    {CLASS_OPTS.map((c, i) => <option key={i} value={i}>{c}</option>)}
                  </select>
                </div>
                <button
                  className="air-compact-search-btn"
                  onClick={() => { handleSearch(); setSearchOpen(false); }}
                  disabled={searching}
                  style={{
                    height: 44, padding: "0 24px", borderRadius: 10, border: "none",
                    background: searching ? "#9CA3AF" : "#1E3A5F",
                    color: "#fff", fontSize: 13, fontWeight: 700,
                    cursor: searching ? "not-allowed" : "pointer", whiteSpace: "nowrap",
                  }}
                >
                  {searching ? "Searching..." : "Search"}
                </button>
              </div>
              </div>
            </div>

            {tripResults.length > 0 && (
              <div className="air-mobile-filter-toggle">
                <Readout size={12} weight={500} color="#9CA3AF">{filteredResultCount} of {allFlights.length} flights</Readout>
                <button onClick={() => setFiltersOpen(true)} className="air-mobile-filter-btn">
                  <IcSliders size={14} /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                </button>
              </div>
            )}

            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#DC2626", fontSize: 14 }}>
                {error}
              </div>
            )}

            {tripResults.length === 0 ? (
              <div style={{ textAlign: "center", padding: 60, color: "#9CA3AF", fontSize: 16 }}>
                No flights found for this search.
              </div>
            ) : (
              tripResults.map((trip, tripIdx) => {
                const rawFlights = trip.Flights || [];
                const shownFlights = sortFlights(rawFlights.filter(matchesFilters));
                return (
                  <div key={tripIdx} style={{ marginBottom: 24 }}>
                    {form.tripType === 1 && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                        {tripIdx === 0 ? `Onward — ${form.from.toUpperCase()} to ${form.to.toUpperCase()}` : `Return — ${form.to.toUpperCase()} to ${form.from.toUpperCase()}`}
                      </div>
                    )}
                    {rawFlights.length === 0 && (
                      <div style={{ color: "#9CA3AF", fontSize: 14, padding: "20px 0" }}>No flights for this leg.</div>
                    )}
                    {rawFlights.length > 0 && shownFlights.length === 0 && (
                      <div style={{ textAlign: "center", padding: "28px 0", color: "#9CA3AF", fontSize: 13 }}>
                        No flights match your filters for this leg.{" "}
                        <button
                          onClick={() => setFilters(emptyFilters())}
                          style={{ background: "none", border: "none", color: "#3B82F6", fontWeight: 700, cursor: "pointer", textDecoration: "underline", padding: 0, fontSize: 13 }}
                        >
                          Clear filters
                        </button>
                      </div>
                    )}
                    {shownFlights.map((fl, fi) => (
                      <FlightCard
                        key={fl.Flight_Key || fi}
                        flight={fl}
                        provider={provider}
                        selected={selectedFlights[tripIdx]?.Flight_Key === fl.Flight_Key}
                        onSelect={(f) => setSelectedFlights((p) => {
                          if (p[tripIdx]?.Flight_Key === f.Flight_Key) {
                            const next = { ...p };
                            delete next[tripIdx];
                            return next;
                          }
                          return { ...p, [tripIdx]: f };
                        })}
                        onViewFareRules={(flight, fare) => {
                          const flSegs = flight.Segments || [];
                          setFareRuleTarget({
                            fareId: String(fare?.Fare_Id ?? ""),
                            domInt: form.travelType === 0 ? "domestic" : "international",
                            title: `${flSegs[0]?.Origin || ""} → ${flSegs[flSegs.length - 1]?.Destination || ""}`,
                          });
                        }}
                      />
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {canProceed && (
          <div className="air-book-bar" style={{
            position: "sticky", bottom: 16, background: "#fff",
            border: "1.5px solid #E5E7EB", borderRadius: 16, padding: "16px 20px",
            boxShadow: "0 -4px 24px rgba(0,0,0,0.10)", display: "flex",
            justifyContent: "space-between", alignItems: "center",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: "#EFF6FF", color: "#3B82F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <IcTicket size={20} />
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#9CA3AF" }}>Total for {totalPax} pax</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "#1E3A5F" }}>₹{totalSelected.toLocaleString("en-IN")}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 1 }}>Including taxes &amp; fees</div>
              </div>
            </div>
            <button
              onClick={handleBook}
              disabled={repricing}
              style={{
                background: repricing ? "#9CA3AF" : "#1E3A5F", color: "#fff",
                border: "none", borderRadius: 12, padding: "12px 28px",
                fontSize: 15, fontWeight: 700, cursor: repricing ? "not-allowed" : "pointer",
              }}
            >
              {repricing ? "Checking price..." : "Continue to Book →"}
            </button>
          </div>
        )}

        {fareRuleTarget && (
          <FareRuleModal
            fareId={fareRuleTarget.fareId}
            domInt={fareRuleTarget.domInt}
            title={fareRuleTarget.title}
            onClose={() => setFareRuleTarget(null)}
          />
        )}

        <style>{`
          .air-mobile-filter-toggle { display: none; }
          /* Same shared-shell double-padding issue as the search-form page — MiniLayout
             already pads the page, this container's own padding was stacking on top of it. */
          @media (max-width: 640px) {
            .air-results-page { padding-left: 4px !important; padding-right: 4px !important; }
          }
          .air-filter-sheet-close { display: none; }
          .air-mobile-filter-btn {
            display: flex; align-items: center; gap: 6px;
            background: #F9FAFB; border: 1.5px solid #E5E7EB; border-radius: 20px;
            padding: 6px 14px; font-size: 12px; font-weight: 700; color: #1E3A5F; cursor: pointer;
          }
          .air-search-summary { display: none; }
          @media (max-width: 760px) {
            .air-results-grid { flex-direction: column; }
            .air-filter-sidebar { position: static !important; width: 100% !important; flex-basis: auto !important; }

            /* "Modify search" card — collapses to a one-line summary by default; tapping it
               opens the full form as a dropdown panel right below (not an overlay), since this
               card already sits pinned at the top of the results column. */
            .air-search-summary {
              display: flex !important; align-items: center; gap: 10px; cursor: pointer;
            }
            .air-search-body { display: none; }
            .air-search-card.air-search-open .air-search-body { display: block; margin-top: 14px; }

            /* Collapsed by default on mobile — the toggle bar below opens it. */
            .air-filter-sidebar:not(.air-filters-open) { display: none !important; }

            /* Open on mobile = a bottom sheet, not inline content. The wrapper becomes a
               full-screen backdrop; its single child (stopPropagation'd) becomes the sheet card. */
            .air-filter-sidebar.air-filters-open {
              display: flex !important; align-items: flex-end; justify-content: center;
              position: fixed !important; inset: 0 !important; top: 0 !important;
              width: 100% !important; max-height: none !important; overflow-y: visible !important;
              background: rgba(15,23,42,0.45); z-index: 300; padding: 0 !important; cursor: pointer;
            }
            .air-filter-sidebar.air-filters-open > div {
              background: #fff; width: 100%; max-height: 82vh; overflow-y: auto;
              border-radius: 20px 20px 0 0; padding: 16px 16px 24px;
              box-shadow: 0 -8px 30px rgba(0,0,0,0.25); cursor: default;
            }

            .air-mobile-filter-toggle {
              display: flex !important; align-items: center; justify-content: space-between;
              margin-bottom: 14px;
            }
            .air-filter-sheet-close {
              display: flex !important; align-items: center; gap: 6px;
              font-size: 13px; font-weight: 700; color: #6B7280; cursor: pointer;
              margin-bottom: 14px;
            }

            /* "Continue to book" total bar — pin to the real screen bottom instead of
               position:sticky, which was floating over the results list mid-scroll. */
            .air-book-bar {
              position: fixed !important; left: 0 !important; right: 0 !important; bottom: 0 !important;
              border-radius: 0 !important; z-index: 250;
            }
            .air-has-book-bar { padding-bottom: 110px !important; }
          }
          @media (max-width: 680px) {
            .air-compact-fromto-grid { grid-template-columns: 1fr !important; gap: 4px !important; }
            .air-compact-swap { transform: rotate(90deg); padding-bottom: 0 !important; }
            .air-compact-pax-grid { grid-template-columns: repeat(2, minmax(0,1fr)) !important; }
            .air-compact-search-btn { grid-column: 1 / -1; width: 100%; }
          }
          @media (max-width: 560px) {
            .air-card-row { flex-wrap: wrap; }
            .air-card-airline {
              width: 100% !important; flex-direction: row !important;
              align-items: center !important; justify-content: space-between; gap: 10px !important;
            }
            .air-card-airline-icons { flex-shrink: 0; }
            .air-card-route { flex-basis: 100% !important; order: 2; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #E2E8F0; }
            .air-card-divider { display: none; }
            .air-card-price { width: 100% !important; min-width: 0 !important; flex-shrink: 1 !important; order: 3;
              display: flex !important; align-items: center !important; justify-content: space-between !important;
              text-align: left !important; padding-left: 0 !important; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #E2E8F0; }
          }
        `}</style>
      </div>
    );
  }

  // ─── Search form (+ embedded booking history below) ────────────
  return (
    <div className="air-form-page" style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px", fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif" }}>
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ margin: "0 0 4px", fontSize: 26, fontWeight: 900, color: "#1E3A5F" }}>✈ Book Flights</h1>
          <p style={{ margin: 0, fontSize: 14, color: "#9CA3AF" }}>Search domestic &amp; international flights</p>
        </div>

        <div className="air-form-card" style={{ background: "#fff", borderRadius: 20, border: "1px solid #E5E7EB", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
        {/* Trip type tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
          {["One Way", "Round Trip"].map((label, i) => (
            <button
              key={i}
              onClick={() => set("tripType", i)}
              style={{
                padding: "8px 20px", borderRadius: 20, fontSize: 13, fontWeight: 600,
                border: "1.5px solid", cursor: "pointer", transition: "all 0.15s",
                borderColor: form.tripType === i ? "#1E3A5F" : "#E5E7EB",
                background: form.tripType === i ? "#1E3A5F" : "#fff",
                color: form.tripType === i ? "#fff" : "#6B7280",
              }}
            >
              {label}
            </button>
          ))}

          {/* Provider toggle removed — both Air1 (WeFlyGo) and Air2 (Nextra) buttons hidden,
              Nextra is used by default via the `provider` constant above. */}

          <button
            onClick={() => set("travelType", form.travelType === 0 ? 1 : 0)}
            style={{
              marginLeft: "auto", padding: "8px 16px", borderRadius: 20, fontSize: 12,
              fontWeight: 600, border: "1.5px solid #E5E7EB", cursor: "pointer",
              background: "#F9FAFB", color: "#374151",
            }}
          >
            {form.travelType === 0 ? "🇮🇳 Domestic" : "🌍 International"}
          </button>
        </div>

        {/* From / To */}
        <div className="air-fromto-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "end", marginBottom: 16 }}>
          <AirportInput label="From" value={form.from} onChange={(v) => set("from", v)} airports={form.travelType === 0 ? AIRPORTS : INTERNATIONAL_AIRPORTS} />
          <div className="air-swap-btn" style={{ paddingBottom: 10, fontSize: 20, color: "#9CA3AF", textAlign: "center", cursor: "pointer", userSelect: "none" }}
            onClick={() => { const t = form.from; set("from", form.to); set("to", t); }}
            title="Swap"
          >
            ⇌
          </div>
          <AirportInput label="To" value={form.to} onChange={(v) => set("to", v)} airports={form.travelType === 0 ? AIRPORTS : INTERNATIONAL_AIRPORTS} />
        </div>

        {/* Dates */}
        <div style={{ display: "grid", gridTemplateColumns: form.tripType === 1 ? "repeat(2, minmax(0, 1fr))" : "1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Departure
            </label>
            <input
              type="date" value={form.travelDate} min={today}
              onChange={(e) => set("travelDate", e.target.value)}
              style={{ width: "100%", height: 44, borderRadius: 10, border: "1.5px solid #D1D5DB", padding: "0 14px", fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box" }}
            />
          </div>
          {form.tripType === 1 && (
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Return
              </label>
              <input
                type="date" value={form.returnDate} min={form.travelDate}
                onChange={(e) => set("returnDate", e.target.value)}
                style={{ width: "100%", height: 44, borderRadius: 10, border: "1.5px solid #D1D5DB", padding: "0 14px", fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box" }}
              />
            </div>
          )}
        </div>

        {/* Passengers */}
        <div className="air-pax-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 20 }}>
          <Counter label="Adults" value={form.adults} min={1} max={9} onChange={(v) => set("adults", v)} />
          <Counter label="Children (2-11)" value={form.children} min={0} max={8} onChange={(v) => set("children", v)} />
          <Counter label="Infants (<2)" value={form.infants} min={0} max={4} onChange={(v) => set("infants", v)} />
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#6B7280", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Class</label>
            <select
              value={form.classOfTravel}
              onChange={(e) => set("classOfTravel", Number(e.target.value))}
              style={{ width: "100%", height: 44, borderRadius: 10, border: "1.5px solid #D1D5DB", padding: "0 12px", fontSize: 13, color: "#111827", background: "#fff", outline: "none", boxSizing: "border-box" }}
            >
              {CLASS_OPTS.map((c, i) => <option key={i} value={i}>{c}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#DC2626", fontSize: 14 }}>
            {error}
          </div>
        )}

        <button
          onClick={handleSearch}
          disabled={searching}
          style={{
            width: "100%", height: 50, borderRadius: 12, border: "none",
            background: searching ? "#9CA3AF" : "#1E3A5F",
            color: "#fff", fontSize: 16, fontWeight: 700,
            cursor: searching ? "not-allowed" : "pointer",
            boxShadow: "0 4px 12px rgba(30,58,95,0.2)",
          }}
        >
          {searching ? "Searching..." : "Search Flights"}
        </button>
      </div>
      </div>

      {/* ─── Booking history, embedded below the search form ─── */}
      <div style={{ marginTop: 32 }}>
        <FlightHistory />
      </div>

      <style>{`
        @media (max-width: 480px) {
          .air-fromto-grid { grid-template-columns: 1fr !important; gap: 4px !important; }
          .air-swap-btn { transform: rotate(90deg); align-self: center; padding-bottom: 0 !important; }
          .air-pax-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
        /* The shared app shell (MiniLayout) already adds its own side padding around every
           page — this page's own padding stacked on top of that was the extra gutter. Cut
           this page's own contribution down on mobile; can't touch the shell from here. */
        @media (max-width: 640px) {
          .air-form-page { padding-left: 4px !important; padding-right: 4px !important; }
          .air-form-card { padding-left: 12px !important; padding-right: 12px !important; }
        }
      `}</style>
    </div>
  );
};

export default FlightSearch;
